import { useState, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Printer, Sparkles } from "lucide-react";
import { submitPledge, getPledgeCount } from "@/lib/api";
import { IMAGES } from "@/data/brand";

export default function SafetyPledgeDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [signed, setSigned] = useState(false);
  const [count, setCount] = useState(null);
  const certRef = useRef(null);

  useEffect(() => { getPledgeCount().then(setCount).catch(() => {}); }, [signed]);

  const fire = () => {
    const end = Date.now() + 700;
    const colors = ["#EF4444", "#2563EB", "#FACC15"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const sign = async () => {
    if (!name.trim()) return;
    try { await submitPledge({ hero_name: name.trim(), age }); } catch (e) {}
    setSigned(true);
    fire();
  };

  const printCert = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Safety Hero Certificate</title>
      <style>body{font-family:Georgia,serif;text-align:center;padding:60px;border:16px solid #EF4444;margin:20px}
      h1{font-size:42px;color:#DC2626;margin:10px}h2{font-size:60px;margin:20px}p{font-size:20px;color:#333}</style></head>
      <body><p>THE SAFETY QUEST proudly certifies</p><h2>${name}</h2>
      <h1>OFFICIAL SAFETY HERO</h1>
      <p>For the promise to Stop-Drop-Roll, Buckle Up, Look Both Ways, and help keep everyone safe.</p>
      <p style="margin-top:40px">— Bobby &amp; Pluggy</p></body></html>`);
    w.document.close();
    w.print();
  };

  const reset = () => { setSigned(false); setName(""); setAge(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild data-testid="open-pledge">{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl border-4 border-sq-ink p-0 overflow-hidden">
        {!signed ? (
          <div className="p-7">
            <DialogHeader>
              <DialogTitle className="font-display text-4xl tracking-wide flex items-center gap-2">
                <ShieldCheck className="text-sq-red" /> Become a Safety Hero
              </DialogTitle>
            </DialogHeader>
            <p className="text-sq-ink/70 mt-2 mb-5">
              Sign the pledge to promise you'll practice your safety moves — and get your very own certificate!
            </p>
            <div className="space-y-3">
              <Input data-testid="pledge-name" placeholder="Your hero name" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl border-2 border-sq-ink/15" />
              <Input data-testid="pledge-age" placeholder="Your age (optional)" value={age} onChange={(e) => setAge(e.target.value)} className="h-12 rounded-xl border-2 border-sq-ink/15" />
            </div>
            <button data-testid="sign-pledge-btn" onClick={sign} className="mt-5 w-full py-4 rounded-full bg-sq-red text-white font-bold text-lg btn-chunky">
              I Promise — Sign My Pledge!
            </button>
            {count != null && (
              <p className="text-center text-sm text-sq-ink/50 mt-4">
                <Sparkles size={14} className="inline mb-0.5 text-sq-yellow" /> {count.toLocaleString()} heroes have already signed
              </p>
            )}
          </div>
        ) : (
          <div className="p-7 text-center bg-sq-bg">
            <div ref={certRef} className="bg-white rounded-2xl border-4 border-sq-red p-7">
              <img src={IMAGES.pluggy} alt="Pluggy" className="w-20 h-20 object-contain mx-auto" />
              <p className="uppercase tracking-[0.3em] text-xs text-sq-ink/50 mt-2">Official Certificate</p>
              <h3 className="font-display text-3xl tracking-wide mt-2 text-sq-red">Safety Hero</h3>
              <p className="font-round text-2xl font-semibold my-2" data-testid="cert-name">{name}</p>
              <p className="text-sm text-sq-ink/60">has promised to Stop-Drop-Roll, Buckle Up & Look Both Ways!</p>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="print-cert-btn" onClick={printCert} className="flex-1 py-3 rounded-full bg-sq-blue text-white font-bold btn-chunky flex items-center justify-center gap-2">
                <Printer size={18} /> Print
              </button>
              <button onClick={reset} className="flex-1 py-3 rounded-full bg-sq-ink text-white font-bold btn-chunky">
                Sign Another
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
